import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiFetch, apiFetchBlob } from './http'
import type { Mealtime, OrderBatchStatus } from './school-orders'

export type FamilyOrderIssueCode =
  | 'MISSING_ITEM'
  | 'WRONG_ITEM'
  | 'QUALITY_ISSUE'
  | 'DIETARY_OR_ALLERGEN'
  | 'CHARGED_INCORRECTLY'
  | 'STUDENT_ABSENT_OR_DID_NOT_RECEIVE'
  | 'OTHER'

export interface FamilyOrderIssueStatus {
  id: string
  status: string
  decisionReason: string | null
  updatedAt: string
}

/**
 * Server-derived life-cycle state per line / per order: `cancelled` (line or
 * batch voided) and `refunded` (gross charge fully credited back) rows render
 * muted with a badge — never as live paid meals — and drop out of the
 * already-ordered counts. Optional on the wire for stale-cache tolerance;
 * absent means `active`.
 */
export type FamilyOrderProjectedState = 'active' | 'cancelled' | 'refunded'

export interface FamilyOrderItemWire {
  orderBatchId: string
  orderItemId: string
  studentUserId: string | null
  studentName: string | null
  menuItemId: string
  /** Set when this placed line is a doctor's-note special-diet line. */
  specialDietAccommodationId?: string | null
  title: string
  /**
   * True when a chef pre-approved component swap re-rendered `title` — the
   * kitchen is serving a replacement. Clients that resolve names from the
   * live menu must prefer `title` for these lines (the menu still carries
   * the original component).
   */
  substituted?: boolean
  imageUri: string | null
  quantity: number
  /** Derived line state — see {@link FamilyOrderProjectedState}. */
  lineState?: FamilyOrderProjectedState
  pricing: {
    grossCents: number
    subsidyCents: number
    outOfPocketCents: number
    subtotalCents: number
    taxesCents: number
    discountsCents: number
    totalCents: number
    currency: string
  }
  submitted: boolean
  editable: boolean
  deadlineAt: string | null
  issueStatus: FamilyOrderIssueStatus | null
}

export interface FamilyOrderWire {
  orderedBy?: 'PARENT' | 'SCHOOL_ADMIN'
  sharedReadOnly?: boolean
  orderBatchId: string
  accountId: string
  organizationId: string
  serviceLocationId: string
  serviceDate: string
  mealtime: Mealtime
  status: OrderBatchStatus
  paymentStatus: string
  /** Placed by the auto-order runner (vs by hand). Optional for stale caches. */
  isAutoOrder?: boolean
  /** Derived order state — see {@link FamilyOrderProjectedState}. */
  state?: FamilyOrderProjectedState
  /** Set on a payment-pending DRAFT: when the automatic re-submit runs. */
  paymentRetryAt: string | null
  subtotalCents: number
  taxesCents: number
  discountsCents: number
  sponsoredCents: number
  totalCents: number
  currency: string
  submittedAt: string | null
  deadlineAt: string | null
  editable: boolean
  items: FamilyOrderItemWire[]
}

export interface FamilyOrdersResponse {
  orders: FamilyOrderWire[]
  generatedAt: string
}

export interface ReplaceFamilyOrderInput {
  orderBatchId: string
  items: Array<{
    menuItemId: string
    studentUserId: string
    quantity: number
    selections?: unknown
    selectionFingerprint?: string | null
    /** Doctor's-note special-diet line — server enforces grant + daily cap. */
    specialDietAccommodationId?: string | null
  }>
}

export interface ReplaceFamilyOrderResponse {
  order: FamilyOrderWire
  deltaCents: number
}

export interface CreateFamilyOrderIssueInput {
  orderBatchId: string
  orderItemId?: string | null
  issueCode: FamilyOrderIssueCode
  parentNote?: string | null
}

export interface CreateFamilyOrderIssueResponse {
  ticket: {
    id: string
    status: string
    decisionReason: string | null
    createdAt: string
  }
}

// ---------------------------------------------------------------------------
// Personalized price preview — server-computed gross/subsidy/net for a set
// of (menuItem, student, quantity) lines on one (serviceDate, mealtime)
// sitting. The per-sitting subsidy allowance is CROSS-BATCH server state
// (an already-submitted subsidized meal consumes it), so clients must
// render this response instead of computing subsidies locally.
// ---------------------------------------------------------------------------

/**
 * `submit` (default): shared allowance walk — totals match what
 * submitting these exact lines would charge (checkout). `browse`: each
 * line priced as if it were the only one ordered (browse rows).
 */
export type FamilyOrderPricePreviewMode = 'submit' | 'browse'

export interface FamilyOrderPricePreviewLineInput {
  menuItemId: string
  studentUserId: string
  quantity: number
  /** Special-diet axis — echoed back on the response line. */
  specialDietAccommodationId?: string | null
}

export interface FamilyOrderPricePreviewLineWire {
  menuItemId: string
  studentUserId: string
  quantity: number
  /** Echo of the request line's special-diet axis (null for regular lines). */
  specialDietAccommodationId?: string | null
  /** Per-unit gross (list) price from the fresh service-date resolve. */
  grossUnitCents: number
  /** LINE-total cents the school/program covers. */
  sponsoredCents: number
  /** LINE-total cents the family pays. */
  netCents: number
  /** A school-paid coverage cap left the family paying the overage. */
  capApplied: boolean
  /** The per-sitting subsidy allowance ran out before this line/unit. */
  allowanceExhausted: boolean
  currency: string
}

export interface FamilyOrderPricePreviewTotals {
  grossCents: number
  subsidyCents: number
  netCents: number
  /**
   * Server-computed sales tax on top of `netCents` (campus
   * `Location.taxRateBps`; $0 when no rate is configured). In `submit`
   * mode `netCents + taxCents` is exactly what submit will charge.
   */
  taxCents: number
}

/**
 * Per-student allowance state for the previewed sitting. Prior-order
 * units always count; in `submit` mode the given lines' subsidized
 * units count too. A student absent from the list has no applicable
 * subsidy (render gross).
 */
export interface FamilyOrderPricePreviewStudentWire {
  studentUserId: string
  subsidizedMealsPerSitting: number
  subsidizedUnitsUsed: number
  remainingSubsidizedUnits: number
}

export interface FamilyOrderPricePreviewResponse {
  lines: FamilyOrderPricePreviewLineWire[]
  totals: FamilyOrderPricePreviewTotals
  students: FamilyOrderPricePreviewStudentWire[]
}

export interface FamilyOrderPricePreviewInput {
  serviceLocationId: string
  /** ISO instant for the service day. */
  serviceDate: string
  mealtime: Mealtime
  mode?: FamilyOrderPricePreviewMode
  lines: FamilyOrderPricePreviewLineInput[]
}

export const familyOrderPricePreviewQueryKey = (vars: FamilyOrderPricePreviewInput) =>
  [
    'family-order-price-preview',
    vars.serviceLocationId,
    vars.serviceDate,
    vars.mealtime,
    vars.mode ?? 'submit',
    vars.lines
      .map(
        (line) => `${line.studentUserId}:${line.menuItemId}:${line.quantity}:${line.specialDietAccommodationId ?? ''}`,
      )
      .sort()
      .join('|'),
  ] as const

export async function fetchFamilyOrderPricePreview(
  vars: FamilyOrderPricePreviewInput,
  signal?: AbortSignal,
): Promise<FamilyOrderPricePreviewResponse> {
  return apiFetch<FamilyOrderPricePreviewResponse>('/api/family-orders/price-preview', {
    method: 'POST',
    body: vars,
    signal,
  })
}

export const familyOrdersQueryKey = (vars: { weekStart: string; studentId?: string | null }) =>
  ['family-orders', vars.weekStart, vars.studentId ?? 'all'] as const

export async function fetchFamilyOrders(
  vars: { weekStart: string; studentId?: string | null },
  signal?: AbortSignal,
): Promise<FamilyOrdersResponse> {
  return apiFetch<FamilyOrdersResponse>('/api/family-orders', {
    query: {
      weekStart: vars.weekStart,
      ...(vars.studentId ? { studentId: vars.studentId } : {}),
    },
    signal,
  })
}

export async function replaceFamilyOrder(vars: ReplaceFamilyOrderInput): Promise<ReplaceFamilyOrderResponse> {
  const { orderBatchId, ...body } = vars
  return apiFetch<ReplaceFamilyOrderResponse>(`/api/family-orders/${encodeURIComponent(orderBatchId)}/replace`, {
    method: 'POST',
    body,
  })
}

export async function createFamilyOrderIssue(
  vars: CreateFamilyOrderIssueInput,
): Promise<CreateFamilyOrderIssueResponse> {
  return apiFetch<CreateFamilyOrderIssueResponse>('/api/family-orders/issues', {
    method: 'POST',
    body: vars,
  })
}

export function useFamilyOrdersQuery(vars: { weekStart: string; studentId?: string | null; enabled?: boolean }) {
  return useQuery<FamilyOrdersResponse>({
    queryKey: familyOrdersQueryKey(vars),
    queryFn: ({ signal }) => fetchFamilyOrders(vars, signal),
    enabled: vars.enabled !== false,
  })
}

/** Under `['family-orders']`, so every order mutation's invalidation refreshes it too. */
export const familyOrdersRangeQueryKey = (vars: { from: string; to: string }) =>
  ['family-orders', 'range', vars.from, vars.to] as const

/** Submitted orders across an inclusive range: the parent print of a week or month (PROD-1064). */
export async function fetchFamilyOrdersRange(
  vars: { from: string; to: string },
  signal?: AbortSignal,
): Promise<FamilyOrdersResponse> {
  return apiFetch<FamilyOrdersResponse>('/api/family-orders', { query: { from: vars.from, to: vars.to }, signal })
}

export function useFamilyOrdersRangeQuery(vars: { from: string; to: string; enabled?: boolean }) {
  return useQuery<FamilyOrdersResponse>({
    queryKey: familyOrdersRangeQueryKey(vars),
    queryFn: ({ signal }) => fetchFamilyOrdersRange(vars, signal),
    enabled: vars.enabled !== false,
    staleTime: 30_000,
  })
}

export interface FamilyPrintPdfRequest {
  title: string
  /** The rendered print pages (sanitized again on the server). */
  markup: string
  /** The `/assets/index-<hash>.css` this page loaded; null under the Vite dev server. */
  cssHref: string | null
  /** A month prints landscape. */
  landscape: boolean
  startDate: string
  endDate: string
}

/**
 * Render the parent print as a PDF; iPhone and iPad hand it to the share
 * sheet. Gzipped like the chef packet: parents print from phones.
 */
export async function fetchFamilyPrintPdf(
  req: FamilyPrintPdfRequest,
  signal?: AbortSignal,
): Promise<{ blob: Blob; pages: number | null }> {
  const { blob, headers } = await apiFetchBlob('/api/family-orders/print/pdf', {
    method: 'POST',
    body: req,
    gzip: true,
    expectContentType: 'application/pdf',
    signal,
  })
  const magic = new Uint8Array(await blob.slice(0, 5).arrayBuffer())
  if (String.fromCharCode(...magic) !== '%PDF-') {
    throw new ApiError(200, { error: 'unexpected_response', message: 'The print came back empty.' })
  }
  const pages = Number(headers.get('X-Packet-Pages'))
  return {
    blob: blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }),
    pages: Number.isInteger(pages) && pages > 0 ? pages : null,
  }
}

export function useReplaceFamilyOrderMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: replaceFamilyOrder,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['family-orders'] })
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
      // The edit may have consumed / freed the per-sitting subsidy
      // allowance — personalized previews must recompute.
      void qc.invalidateQueries({ queryKey: ['family-order-price-preview'] })
    },
  })
}

export function useCreateFamilyOrderIssueMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createFamilyOrderIssue,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['family-orders'] })
    },
  })
}

export async function cancelFamilyOrderPaymentRetry(vars: {
  orderBatchId: string
}): Promise<{ orderBatchId: string; status: string }> {
  return apiFetch(`/api/family-orders/${encodeURIComponent(vars.orderBatchId)}/cancel-payment-retry`, {
    method: 'POST',
  })
}

export function useCancelPaymentRetryMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelFamilyOrderPaymentRetry,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['family-orders'] })
    },
  })
}

/**
 * "Complete order now" on a payment-pending DRAFT — retries the wallet
 * debit immediately instead of waiting for the automatic 24h retry
 * (launch-gap 09: the parent just added funds and returned via
 * `?returnTo=`). A 402 `payment_error` means the balance STILL doesn't
 * cover it; the batch stays pending (never voided by a manual retry).
 */
export async function retryFamilyOrderPayment(vars: {
  orderBatchId: string
}): Promise<{ orderBatchId: string; status: string }> {
  return apiFetch(`/api/family-orders/${encodeURIComponent(vars.orderBatchId)}/retry-payment`, {
    method: 'POST',
  })
}

export function useRetryPaymentMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: retryFamilyOrderPayment,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['family-orders'] })
      // The debit landed — the wallet balance and the per-sitting
      // subsidy allowances both moved.
      void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
      void qc.invalidateQueries({ queryKey: ['family-order-price-preview'] })
    },
  })
}
